import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  FormField,
  Input,
  LoadingState,
} from '@sakhtar/design-system/react';
import {
  listOperatorAiAssistantInterest,
  type AiAssistantInterest,
} from '../../lib/operatorApi.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { OperatorPage } from './OperatorShell.js';
import { OrgUserAvatar, WithAvatar } from './OperatorAvatars.js';

export function AiInterestPage() {
  const [rows, setRows] = useState<readonly AiAssistantInterest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  async function load() {
    setError(null);
    try {
      const data = await listOperatorAiAssistantInterest();
      setRows(data);
    } catch {
      setError('بارگذاری درخواست‌های دستیار هوش مصنوعی ناموفق بود.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.displayName.toLowerCase().includes(query) || r.tenantSlug.toLowerCase().includes(query),
    );
  }, [rows, searchQuery]);

  return (
    <OperatorPage
      title="پیش‌ثبت‌نام‌های دستیار هوش مصنوعی"
      actions={
        <Button variant="secondary" type="button" onClick={() => void load()}>
          بروزرسانی فهرست
        </Button>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {/* خلاصه آماری */}
      {rows ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <Card title="مجموع درخواست‌های ثبت‌شده">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#3b82f6' }}>
              {rows.length}
            </span>
          </Card>
        </div>
      ) : null}

      {/* فیلتر و جستجو */}
      {rows !== null && rows.length > 0 ? (
        <Card>
          <FilterBar>
            <FormField label="جستجو در متقاضیان">
              <Input
                placeholder="نام متقاضی یا نامک سازمان..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </FormField>
            {searchQuery ? (
              <Button variant="secondary" type="button" onClick={() => setSearchQuery('')}>
                پاک کردن جستجو
              </Button>
            ) : null}
          </FilterBar>
        </Card>
      ) : null}

      {rows === null ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="هنوز درخواستی برای پیش‌ثبت‌نام دستیار هوش مصنوعی ثبت نشده است." />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="هیچ درخواستی با این مشخصات یافت نشد." />
      ) : (
        <DataTable
          columns={[
            {
              key: 'displayName',
              header: 'نام متقاضی / کاربر',
              render: (row) => (
                <WithAvatar
                  avatar={
                    <OrgUserAvatar slug={row.tenantSlug} accountId={null} name={row.displayName} />
                  }
                >
                  <strong>{row.displayName}</strong>
                </WithAvatar>
              ),
            },
            {
              key: 'tenantSlug',
              header: 'نامک سازمان',
              render: (row) => (
                <code
                  dir="ltr"
                  style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'var(--sk-surface-inset, #f3f4f6)',
                  }}
                >
                  {row.tenantSlug}
                </code>
              ),
            },
            {
              key: 'requestedAt',
              header: 'تاریخ و زمان درخواست',
              render: (row) => (
                <span>
                  {displayJalaliDate(row.requestedAt)}{' '}
                  <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                    (
                    {new Date(row.requestedAt).toLocaleTimeString('fa-IR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    )
                  </span>
                </span>
              ),
            },
          ]}
          rows={[...filteredRows]}
          rowKey={(row) => `${row.requestedAt}:${row.tenantSlug}:${row.displayName}`}
        />
      )}
      <p style={{ color: 'var(--sk-fg-muted)', fontSize: 13, marginTop: 16 }}>
        این فهرست شامل کاربرانی است که در صفحه معرفی دستیار هوش مصنوعی روی «به من اطلاع بده» کلیک
        کرده‌اند.
      </p>
    </OperatorPage>
  );
}
